/**
 * Git contribution workflow — commit, push, PR for repo changes.
 *
 * Enables editing assets in cloned repos and contributing changes back upstream.
 */

import { spawnSync } from "node:child_process";
import { existsSync } from "node:fs";
import { join } from "node:path";

// ── Data types ────────────────────────────────────────────────────────────────

/**
 * A single file that has been modified in a repository working tree.
 *
 * @docLink packages/asset-manager/api-reference#contrib
 */
export interface LocalChange {
  /** Short git status code (e.g. `"M"`, `"A"`, `"D"`). */
  status: string;
  /** Path to the changed file, relative to the repository root. */
  path: string;
}

/**
 * Comparison between a local repository clone and its upstream remote.
 *
 * @docLink packages/asset-manager/api-reference#contrib
 */
export interface ForkComparison {
  /** Number of commits the local clone is ahead of the upstream branch. */
  ahead: number;
  /** Number of commits the local clone is behind the upstream branch. */
  behind: number;
  /** Files changed in local commits not yet pushed to upstream. */
  aheadFiles: string[];
  /** Files changed in upstream commits not yet pulled locally. */
  behindFiles: string[];
  /** Error message if the comparison could not be performed. */
  error?: string;
}

/**
 * Result of a repository sync operation.
 *
 * @docLink packages/asset-manager/api-reference#contrib
 */
export interface SyncResult {
  /** Number of catalog entries found after syncing. */
  count: number;
  /** Error message when the sync failed. */
  error?: string;
}

// ── Change detection ──────────────────────────────────────────────────────────

/**
 * List uncommitted changes in a local repository clone.
 *
 * @param repoDir - Absolute path to the repository root.
 * @returns Array of changed files with their git status codes. Returns `[]` when the
 *   directory is not a git repository or `git status` fails.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function localChanges(repoDir: string): LocalChange[] {
  if (!existsSync(join(repoDir, ".git"))) return [];
  const r = spawnSync("git", ["-C", repoDir, "status", "--porcelain"], { encoding: "utf8" });
  if (r.status !== 0) return [];
  return r.stdout
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => ({ status: l.slice(0, 2).trim(), path: l.slice(3).trim() }));
}

/**
 * Return `true` when the repository has uncommitted changes.
 *
 * @param repoDir - Absolute path to the repository root.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function hasLocalChanges(repoDir: string): boolean {
  return localChanges(repoDir).length > 0;
}

/**
 * Return the unified diff of all uncommitted changes in the repository.
 *
 * @param repoDir - Absolute path to the repository root.
 * @returns Unified diff output from `git diff HEAD`, or `""` when there are no changes
 *   or the directory is not a git repository.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function getDiff(repoDir: string): string {
  if (!existsSync(join(repoDir, ".git"))) return "";
  const r = spawnSync("git", ["-C", repoDir, "diff", "HEAD"], { encoding: "utf8" });
  return r.status === 0 ? r.stdout : "";
}

// ── Commit / push / PR ───────────────────────────────────────────────────────

/**
 * Stage all changes and create a commit in the repository clone.
 *
 * If `branch` is provided, creates or switches to that branch before committing.
 *
 * @param repoDir - Absolute path to the repository root.
 * @param message - Git commit message.
 * @param branch - Branch name to create or switch to before committing.
 * @returns `[true, commitOutput]` on success, `[false, errorMessage]` on failure.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function commitChanges(
  repoDir: string,
  message: string,
  branch?: string,
): [boolean, string] {
  if (branch) {
    const br = spawnSync("git", ["-C", repoDir, "checkout", "-b", branch], { encoding: "utf8" });
    if (br.status !== 0) {
      const sw = spawnSync("git", ["-C", repoDir, "checkout", branch], { encoding: "utf8" });
      if (sw.status !== 0) return [false, `Failed to create/switch to branch: ${branch}`];
    }
  }
  const add = spawnSync("git", ["-C", repoDir, "add", "-A"], { encoding: "utf8" });
  if (add.status !== 0) return [false, "git add failed"];
  const commit = spawnSync("git", ["-C", repoDir, "commit", "-m", message], { encoding: "utf8" });
  if (commit.status !== 0) return [false, commit.stderr?.trim() || "git commit failed"];
  return [true, commit.stdout.trim()];
}

/**
 * Push a local branch to a remote URL (adds it as a `"fork"` git remote).
 *
 * @param repoDir - Absolute path to the repository root.
 * @param remoteUrl - Remote URL to push to (e.g. a GitHub fork's HTTPS/SSH URL).
 * @param branch - Local branch name to push.
 * @returns `[true, ""]` on success, `[false, errorMessage]` on failure.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function pushToRemote(
  repoDir: string,
  remoteUrl: string,
  branch: string,
): [boolean, string] {
  spawnSync("git", ["-C", repoDir, "remote", "remove", "fork"], { stdio: "pipe" });
  const addR = spawnSync("git", ["-C", repoDir, "remote", "add", "fork", remoteUrl], {
    encoding: "utf8",
  });
  if (addR.status !== 0) return [false, "Failed to add fork remote"];
  const push = spawnSync("git", ["-C", repoDir, "push", "fork", `${branch}:${branch}`], {
    encoding: "utf8",
  });
  if (push.status !== 0) return [false, push.stderr?.trim() || "git push failed"];
  return [true, ""];
}

/**
 * Open a pull request on GitHub using the `gh` CLI.
 *
 * @param upstreamUrl - GitHub repository URL or `owner/repo` identifier.
 * @param title - Pull request title.
 * @param body - Pull request description (Markdown).
 * @param head - Branch name containing the changes.
 * @param base - Base branch to merge into. Defaults to `"main"`.
 * @returns `[true, prUrl]` on success, `[false, errorMessage]` on failure.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function createPr(
  upstreamUrl: string,
  title: string,
  body: string,
  head: string,
  base = "main",
): [boolean, string] {
  const r = spawnSync(
    "gh",
    [
      "pr",
      "create",
      "--repo",
      upstreamUrl,
      "--title",
      title,
      "--body",
      body,
      "--head",
      head,
      "--base",
      base,
    ],
    { encoding: "utf8" },
  );
  if (r.status !== 0) return [false, r.stderr?.trim() || "gh pr create failed"];
  return [true, r.stdout.trim()];
}

// ── Fork comparison ──────────────────────────────────────────────────────────

/**
 * Compare a local repository clone with its upstream remote.
 *
 * Temporarily registers `upstream` as the `"upstream"` git remote, fetches the
 * specified branch, and counts diverging commits in both directions.
 *
 * @param repoDir - Absolute path to the local repository clone.
 * @param upstream - Remote URL of the upstream repository.
 * @param branch - Branch to compare against. Defaults to `"main"`.
 * @returns {@link ForkComparison} with ahead/behind counts and changed file lists.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function compareWithUpstream(
  repoDir: string,
  upstream: string,
  branch = "main",
): ForkComparison {
  const base: ForkComparison = { ahead: 0, behind: 0, aheadFiles: [], behindFiles: [] };

  spawnSync("git", ["-C", repoDir, "remote", "remove", "upstream"], { stdio: "pipe" });
  const addR = spawnSync("git", ["-C", repoDir, "remote", "add", "upstream", upstream], {
    stdio: "pipe",
  });
  if (addR.status !== 0) return { ...base, error: "Failed to add upstream remote" };

  const fetch = spawnSync("git", ["-C", repoDir, "fetch", "upstream", branch], { stdio: "pipe" });
  if (fetch.status !== 0) return { ...base, error: "Failed to fetch upstream" };

  const count = spawnSync(
    "git",
    ["-C", repoDir, "rev-list", "--left-right", "--count", `HEAD...upstream/${branch}`],
    { encoding: "utf8" },
  );
  if (count.status === 0) {
    const parts = count.stdout.trim().split(/\s+/);
    base.ahead = parseInt(parts[0] ?? "0", 10);
    base.behind = parseInt(parts[1] ?? "0", 10);
  }

  if (base.ahead > 0) {
    const diff = spawnSync(
      "git",
      ["-C", repoDir, "diff", "--name-only", `upstream/${branch}...HEAD`],
      { encoding: "utf8" },
    );
    if (diff.status === 0) base.aheadFiles = diff.stdout.trim().split("\n").filter(Boolean);
  }

  if (base.behind > 0) {
    const diff = spawnSync(
      "git",
      ["-C", repoDir, "diff", "--name-only", `HEAD...upstream/${branch}`],
      { encoding: "utf8" },
    );
    if (diff.status === 0) base.behindFiles = diff.stdout.trim().split("\n").filter(Boolean);
  }

  return base;
}

/**
 * List files added or modified locally compared with an upstream branch.
 *
 * Fetches the upstream branch and returns only the files that exist in the local
 * branch but not in upstream (additions and modifications, not deletions).
 *
 * @param repoDir - Absolute path to the local repository clone.
 * @param upstream - Remote URL of the upstream repository.
 * @param branch - Upstream branch to compare against. Defaults to `"main"`.
 * @returns Object with `files` list and an optional `error` string on failure.
 * @docLink packages/asset-manager/api-reference#contrib
 */
export function listAdditions(
  repoDir: string,
  upstream: string,
  branch = "main",
): { files: string[]; error?: string } {
  spawnSync("git", ["-C", repoDir, "remote", "remove", "upstream"], { stdio: "pipe" });
  spawnSync("git", ["-C", repoDir, "remote", "add", "upstream", upstream], { stdio: "pipe" });
  const fetch = spawnSync("git", ["-C", repoDir, "fetch", "upstream", branch], { stdio: "pipe" });
  if (fetch.status !== 0) return { files: [], error: "Failed to fetch upstream" };

  const diff = spawnSync(
    "git",
    ["-C", repoDir, "diff", "--name-status", `upstream/${branch}...HEAD`],
    { encoding: "utf8" },
  );
  if (diff.status !== 0) return { files: [], error: "Failed to diff" };

  const files = diff.stdout
    .trim()
    .split("\n")
    .filter((l) => l.startsWith("A") || l.startsWith("M"))
    .map((l) => l.split("\t")[1] ?? "")
    .filter(Boolean);

  return { files };
}
